import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeeCount() {
  const [employeeCounts, setEmployeeCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRange, setNewRange] = useState('');
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchEmployeeCounts();
  }, []);

  const fetchEmployeeCounts = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/master/employee-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEmployeeCounts(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load employee counts');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newRange.trim()) return;

    setAdding(true);
    try {
      const token = Cookies.get('admin_token');
      await axios.post(`${API}/master/employee-counts`, 
        { range: newRange },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Employee count added successfully');
      setNewRange('');
      setOpen(false);
      fetchEmployeeCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add employee count');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee count range?')) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/master/employee-counts/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Employee count deleted');
      fetchEmployeeCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete employee count');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Employee Count</h1>
          <p className="text-muted-foreground">Manage employee count ranges in the system</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="editorial">
              <Plus className="h-4 w-4 mr-2" /> Add Employee Count
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Employee Count Range</DialogTitle>
              <DialogDescription>Enter employee count range (e.g., 1-10, 11-50, 500+)</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="range">Employee Count Range *</Label>
                  <Input
                    id="range"
                    value={newRange}
                    onChange={(e) => setNewRange(e.target.value)}
                    placeholder="e.g., 1-10, 11-50, 500+"
                    disabled={adding}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={adding}>
                  Cancel
                </Button>
                <Button type="submit" disabled={adding || !newRange.trim()}>
                  {adding ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                  ) : (
                    'Add Range'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Employee Counts List */}
      <Card>
        <CardHeader>
          <CardTitle>All Employee Count Ranges ({employeeCounts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : employeeCounts.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No employee count ranges added yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {employeeCounts.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <span className="font-medium">{item.range}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
